import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr } from "@/lib/api";
import { AREA_UNITS, BHK_OPTIONS, LEAD_TYPES, PROPERTY_TYPES, SOURCES, STAGES, TEMPS, TEMP_STYLE } from "@/lib/constants";
import { fromISTInput, inrCompact, toISTInput } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useAssignableOptions } from "@/hooks/useData";
import { Field, NSelect, PageHeader, Spinner } from "@/components/common";
import { DuplicateDialog } from "@/components/Dialogs";
import DatePicker from "@/components/DatePicker";
import { cn } from "@/lib/utils";

const EMPTY = {
  name: "", phone: "", alt_phone: "", email: "", lead_type: "Buyer", property_type: "", locations: "", budget_min: "", budget_max: "",
  area_min: "", area_max: "", area_unit: "sq ft", bhk: "", purpose: "", source: "Other", temperature: "WARM", stage: "New",
  lost_reason: "", assigned_to: "", next: "", notes: "",
};
const num = (v) => (v === "" || v == null ? null : Number(v));

export const Section = ({ title, children }) => (
  <section className="rounded-2xl border bg-card p-5">
    <h3 className="mb-4 font-display font-bold">{title}</h3>
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
  </section>
);

const Money = ({ value, onChange, testid }) => (
  <div>
    <Input type="number" min="0" inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    {value && <span className="num mt-1 block text-xs text-muted-foreground">{inrCompact(value)}</span>}
  </div>
);

export default function LeadFormPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const assignOpts = useAssignableOptions();
  const [f, setF] = useState({ ...EMPTY, assigned_to: user.id });
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState(null);
  const [phoneDup, setPhoneDup] = useState([]);
  const { data: existing, isLoading } = useQuery({ queryKey: ["lead", id], queryFn: async () => (await api.get(`/leads/${id}`)).data, enabled: !!id });

  useEffect(() => {
    if (existing) {
      setF({
        ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).filter((k) => existing[k] != null).map((k) => [k, existing[k]])),
        locations: (existing.locations || []).join(", "), assigned_to: existing.assigned_to || "", next: toISTInput(existing.next_followup_at),
      });
    }
  }, [existing]);

  const s = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const ev = (k) => (e) => s(k)(e.target.value);

  const checkPhone = async () => {
    if (f.phone.replace(/\D/g, "").length < 10) return setPhoneDup([]);
    try {
      const { data } = await api.get("/leads/check-duplicate", { params: { phone: f.phone, exclude_id: id } });
      setPhoneDup(data.duplicates);
    } catch {
      setPhoneDup([]);
    }
  };

  const save = async (force = false) => {
    setBusy(true);
    const body = {
      name: f.name, phone: f.phone, alt_phone: f.alt_phone, email: f.email, lead_type: f.lead_type, property_type: f.property_type,
      locations: f.locations.split(",").map((x) => x.trim()).filter(Boolean), budget_min: num(f.budget_min), budget_max: num(f.budget_max),
      area_min: num(f.area_min), area_max: num(f.area_max), area_unit: f.area_unit, bhk: f.bhk, purpose: f.purpose, source: f.source,
      temperature: f.temperature, stage: f.stage, lost_reason: f.lost_reason, assigned_to: f.assigned_to || null,
      next_followup_at: fromISTInput(f.next), notes: f.notes,
    };
    try {
      const { data } = id ? await api.put(`/leads/${id}`, body, { params: { force } }) : await api.post("/leads", body, { params: { force } });
      toast.success(id ? "Lead updated" : "Lead added");
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["lead", data.id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      nav(`/leads/${data.id}`, { replace: true });
    } catch (e) {
      if (e.response?.status === 409) setDup(e.response.data.detail);
      else toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (id && isLoading) return <Spinner />;
  return (
    <form onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-6" data-testid="lead-form">
      <Link to={id ? `/leads/${id}` : "/leads"} className="inline-flex items-center gap-1 text-sm text-muted-foreground"><ArrowLeft className="h-4 w-4" />Back</Link>
      <PageHeader title={id ? "Edit lead" : "Add lead"} testid="lead-form-title" />
      <Section title="Contact">
        <Field label="Name" required><Input data-testid="lead-name-input" value={f.name} onChange={ev("name")} required /></Field>
        <Field label="Phone" required>
          <Input data-testid="lead-phone-input" inputMode="tel" value={f.phone} onChange={ev("phone")} onBlur={checkPhone} required />
          {phoneDup.length > 0 && (
            <span className="flex items-start gap-1 text-xs font-medium text-orange-600" data-testid="lead-phone-duplicate-warning">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Already exists: {phoneDup.map((d) => <Link key={d.id} to={`/leads/${d.id}`} target="_blank" className="underline">{d.name}</Link>)}
            </span>
          )}
        </Field>
        <Field label="Alternate phone"><Input data-testid="lead-alt-phone-input" inputMode="tel" value={f.alt_phone} onChange={ev("alt_phone")} /></Field>
        <Field label="Email"><Input data-testid="lead-email-input" type="email" value={f.email} onChange={ev("email")} /></Field>
        <Field label="Lead type"><NSelect testid="lead-type-select" value={f.lead_type} onChange={s("lead_type")} options={LEAD_TYPES} /></Field>
        <Field label="Source"><NSelect testid="lead-source-select" value={f.source} onChange={s("source")} options={SOURCES} /></Field>
      </Section>
      <Section title="Requirement">
        <Field label="Property type"><NSelect testid="lead-property-type-select" value={f.property_type} onChange={s("property_type")} placeholder="Any" options={PROPERTY_TYPES} /></Field>
        <Field label="Preferred locations / sectors" hint="Separate with commas"><Input data-testid="lead-locations-input" value={f.locations} onChange={ev("locations")} placeholder="Sector 66, Aerocity" /></Field>
        <Field label="BHK"><NSelect testid="lead-bhk-select" value={f.bhk} onChange={s("bhk")} placeholder="Any" options={BHK_OPTIONS} /></Field>
        <Field label="Budget min (₹)"><Money testid="lead-budget-min-input" value={f.budget_min} onChange={s("budget_min")} /></Field>
        <Field label="Budget max (₹)"><Money testid="lead-budget-max-input" value={f.budget_max} onChange={s("budget_max")} /></Field>
        <Field label="Purpose"><NSelect testid="lead-purpose-select" value={f.purpose} onChange={s("purpose")} placeholder="—" options={["End use", "Investment"]} /></Field>
        <Field label="Area min"><Input type="number" min="0" data-testid="lead-area-min-input" value={f.area_min} onChange={ev("area_min")} /></Field>
        <Field label="Area max"><Input type="number" min="0" data-testid="lead-area-max-input" value={f.area_max} onChange={ev("area_max")} /></Field>
        <Field label="Area unit"><NSelect testid="lead-area-unit-select" value={f.area_unit} onChange={s("area_unit")} options={AREA_UNITS} /></Field>
      </Section>
      <Section title="Status">
        <Field label="Temperature">
          <div className="flex gap-2">
            {TEMPS.map((t) => (
              <button type="button" key={t} data-testid={`lead-temp-${t.toLowerCase()}-option`} onClick={() => s("temperature")(t)}
                className={cn("h-10 flex-1 rounded-lg border text-xs font-bold transition-opacity", TEMP_STYLE[t], f.temperature !== t && "opacity-35")}>{t}</button>
            ))}
          </div>
        </Field>
        <Field label="Stage"><NSelect testid="lead-stage-form-select" value={f.stage} onChange={s("stage")} options={STAGES} /></Field>
        {f.stage === "Closed Lost" && <Field label="Lost reason" required><Input data-testid="lead-lost-reason-input" value={f.lost_reason} onChange={ev("lost_reason")} required /></Field>}
        <Field label="Assigned to"><NSelect testid="lead-assigned-select" value={f.assigned_to} onChange={s("assigned_to")} placeholder="Unassigned" options={assignOpts} /></Field>
        <Field label="Next follow-up (IST)"><DatePicker withTime testid="lead-next-followup-input" value={f.next} onChange={s("next")} /></Field>
        <Field label="Notes" className="sm:col-span-2 lg:col-span-3"><Textarea data-testid="lead-notes-input" value={f.notes} onChange={ev("notes")} rows={3} /></Field>
      </Section>
      <div className="sticky bottom-20 z-10 flex justify-end gap-2 md:bottom-4">
        <Button type="button" variant="outline" onClick={() => nav(-1)} data-testid="lead-cancel-button">Cancel</Button>
        <Button type="submit" disabled={busy} data-testid="lead-save-button" className="shadow-lg">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{id ? "Save changes" : "Add lead"}</Button>
      </div>
      <DuplicateDialog dup={dup} basePath="/leads" onCancel={() => setDup(null)} onConfirm={() => { setDup(null); save(true); }}
        render={(d) => <><span className="font-semibold">{d.name}</span> · {d.phone} · {d.stage}</>} />
    </form>
  );
}
